import React, { useEffect, useMemo, useState } from "react";
import Meta from "../components/Meta";
import Custominput from "../components/Custominput";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import { useDispatch } from "react-redux";
import kuptoLogo from "../images/logo/kupto logobb-8.png";
import "./styles/profileDetails.css";

import {
  registerUser,
  completeGoogleProfileUser,
} from "../app/features/user/userSlice";

const isValidEmail = (v) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

const normalizeIdentity = (raw) => {
  const v = String(raw || "").trim();
  if (!v) return { identity: "", type: "" };
  if (isValidEmail(v)) return { identity: v.toLowerCase(), type: "email" };
  const cleaned = v.replace(/[^\d+]/g, "");
  return { identity: cleaned, type: "phone" };
};

const maskIdentity = (identity, type) => {
  if (!identity) return "";
  if (type === "email") {
    const [u, d] = identity.split("@");
    if (!u || !d) return identity;
    const head = u.slice(0, 1);
    return `${head}${"*".repeat(Math.max(1, u.length - 1))}@${d}`;
  }
  const digits = identity.replace(/[^\d]/g, "");
  if (digits.length < 4) return identity;
  return `***${digits.slice(-3)}`;
};

const UG_PREFIX = "+256";
const MIN_AGE_YEARS = 13;

const toISODate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const calcAgeYears = (dobISO) => {
  if (!dobISO) return 0;
  const dobDate = new Date(`${dobISO}T00:00:00`);
  if (Number.isNaN(dobDate.getTime())) return 0;

  const today = new Date();
  let age = today.getFullYear() - dobDate.getFullYear();
  const m = today.getMonth() - dobDate.getMonth();

  if (m < 0 || (m === 0 && today.getDate() < dobDate.getDate())) age -= 1;
  return age;
};

const MONTHS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const getDaysInMonth = (year, month) => {
  if (!year || !month) return 31;
  return new Date(Number(year), Number(month), 0).getDate();
};

const buildDobISO = (year, month, day) => {
  if (!year || !month || !day) return "";
  return `${year}-${month}-${day}`;
};

const ProfileDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const stateIdentity = location?.state?.identity || "";
  const stateType = location?.state?.type || "";
  const verified = Boolean(location?.state?.verified);
  const passedPassword = location?.state?.password || "";

  const googleProfile = location?.state?.googleProfile || null;

  const returnToFromState = location?.state?.returnTo;
  const returnTo = useMemo(() => {
    if (typeof returnToFromState === "string" && returnToFromState.trim()) {
      return returnToFromState;
    }
    try {
      const ss = sessionStorage.getItem("kupto_returnTo");
      if (ss && ss.trim()) return ss;
    } catch {}
    return "/";
  }, [returnToFromState]);

  const googleEmail = useMemo(() => {
    return String(googleProfile?.email || "").trim().toLowerCase();
  }, [googleProfile]);

  const googleFirst = useMemo(
    () =>
      String(googleProfile?.firstname || googleProfile?.given_name || "").trim(),
    [googleProfile]
  );

  const googleLast = useMemo(
    () =>
      String(googleProfile?.lastname || googleProfile?.family_name || "").trim(),
    [googleProfile]
  );

  const { identity, type } = useMemo(() => {
    if (googleProfile && googleEmail) {
      return { identity: googleEmail, type: "email" };
    }

    const n = normalizeIdentity(stateIdentity);
    return {
      identity: stateIdentity ? n.identity : "",
      type: stateType || n.type || "",
    };
  }, [stateIdentity, stateType, googleProfile, googleEmail]);

  useEffect(() => {
    try {
      if (returnTo && returnTo !== "/") {
        sessionStorage.setItem("kupto_returnTo", returnTo);
      }
    } catch {}
  }, [returnTo]);

  useEffect(() => {
    if (!identity) {
      navigate("/identification", { replace: true });
      return;
    }

    if (googleProfile) return;

    if (identity && !verified) {
      navigate("/verification-code", {
        state: { identity, type, returnTo },
        replace: true,
      });
      return;
    }

    if (identity && verified && !passedPassword) {
      navigate("/signup", {
        state: { identity, type, verified: true, returnTo },
        replace: true,
      });
    }
  }, [
    identity,
    verified,
    passedPassword,
    navigate,
    type,
    returnTo,
    googleProfile,
  ]);

  const maskedIdentity = useMemo(
    () => maskIdentity(identity, type),
    [identity, type]
  );

  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");

  const [dobMonth, setDobMonth] = useState("");
  const [dobDay, setDobDay] = useState("");
  const [dobYear, setDobYear] = useState("");

  const [phoneLocal, setPhoneLocal] = useState("");
  const [touched, setTouched] = useState({
    fn: false,
    ln: false,
    dob: false,
    phone: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [apiErr, setApiErr] = useState("");

  useEffect(() => {
    if (!googleProfile) return;
    if (googleFirst) setFirstname((prev) => (prev ? prev : googleFirst));
    if (googleLast) setLastname((prev) => (prev ? prev : googleLast));
  }, [googleProfile, googleFirst, googleLast]);

  useEffect(() => {
    if (!identity) return;
    if (type === "phone") {
      const d = String(identity || "").replace(/[^\d]/g, "");
      if (d.startsWith("256")) setPhoneLocal(d.slice(3));
      else if (d.startsWith("0")) setPhoneLocal(d.replace(/^0+/, ""));
      else setPhoneLocal(d);
    }
  }, [identity, type]);

  const todayISO = useMemo(() => toISODate(new Date()), []);
  const currentYear = new Date().getFullYear();

  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear; y >= 1900; y -= 1) {
      years.push(String(y));
    }
    return years;
  }, [currentYear]);

  const dayOptions = useMemo(() => {
    const total = getDaysInMonth(dobYear, dobMonth);
    return Array.from({ length: total }, (_, i) =>
      String(i + 1).padStart(2, "0")
    );
  }, [dobYear, dobMonth]);

  useEffect(() => {
    if (!dobDay) return;
    const total = getDaysInMonth(dobYear, dobMonth);
    if (Number(dobDay) > total) {
      setDobDay("");
    }
  }, [dobDay, dobMonth, dobYear]);

  const dob = useMemo(
    () => buildDobISO(dobYear, dobMonth, dobDay),
    [dobYear, dobMonth, dobDay]
  );

  const fnOk = firstname.trim().length >= 2;
  const lnOk = lastname.trim().length >= 2;

  const dobAge = useMemo(() => calcAgeYears(dob), [dob]);

  const dobInPast = Boolean(dob) &&
    new Date(`${dob}T00:00:00`).getTime() <
      new Date(`${todayISO}T00:00:00`).getTime();

  const dobOldEnough = Boolean(dob) && dobAge >= MIN_AGE_YEARS;
  const dobOk = Boolean(dob) && dobInPast && dobOldEnough;

  const dobError = !dob
    ? "Please select your date of birth."
    : !dobInPast
    ? "Date of birth must be in the past."
    : !dobOldEnough
    ? `You must be at least ${MIN_AGE_YEARS} years old to create an account.`
    : "";

  const phoneLocalDigits = String(phoneLocal || "")
    .replace(/[^\d]/g, "")
    .replace(/^0+/, "");

  const phoneOk = phoneLocalDigits.length >= 9;
  const mobileE164 = `${UG_PREFIX}${phoneLocalDigits}`;
  const phoneLocked = type === "phone";

  const needsPassword = !googleProfile;

  const canSubmit =
    fnOk &&
    lnOk &&
    dobOk &&
    phoneOk &&
    identity &&
    (!needsPassword || passedPassword) &&
    !isLoading;

  const onSubmit = async (e) => {
    e.preventDefault();
    setApiErr("");
    setTouched({ fn: true, ln: true, dob: true, phone: true });

    if (!canSubmit) return;

    try {
      setIsLoading(true);

      if (googleProfile) {
        const payload = {
          firstname: firstname.trim(),
          lastname: lastname.trim(),
          mobile: mobileE164,
          dob,
          email: String(identity).trim().toLowerCase(),
          picture: googleProfile?.picture || googleProfile?.avatar || "",
          provider: "google",
          googleId: googleProfile?.googleId || "",
        };

        const data = await dispatch(
          completeGoogleProfileUser(payload)
        ).unwrap();

        setIsLoading(false);

        if (data?.token || data?.accessToken || data?.user) {
          navigate(returnTo, { replace: true });
          return;
        }

        navigate("/login", {
          replace: true,
          state: {
            identity,
            type: "email",
            returnTo,
            skipRefresh: true,
          },
        });
        return;
      }

      const payload = {
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        mobile: mobileE164,
        password: passedPassword,
        dob,
        ...(type === "email" && identity
          ? { email: String(identity).trim().toLowerCase() }
          : {}),
      };

      await dispatch(registerUser(payload)).unwrap();

      setIsLoading(false);

      navigate("/login", {
        replace: true,
        state: {
          identity: type === "email" ? identity : mobileE164,
          type: type === "email" ? "email" : "phone",
          returnTo,
          skipRefresh: true,
        },
      });
    } catch (err) {
      setIsLoading(false);
      setApiErr(err?.message || "Failed to create account.");
    }
  };

  const backReturnTo = useMemo(() => {
    try {
      return returnTo || sessionStorage.getItem("kupto_returnTo") || "/";
    } catch {
      return returnTo || "/";
    }
  }, [returnTo]);

  return (
    <>
      <Meta title="Profile details" />

      <div className="ku-pd-wrap">
        {isLoading ? (
          <div className="ku-pd-lineLoader" role="status" aria-label="Loading">
            <span className="ku-pd-lineTrack" aria-hidden="true">
              <span className="ku-pd-lineBar" />
            </span>
          </div>
        ) : null}

        <div className="ku-pd-shell">
          <div className={`ku-pd-card ${isLoading ? "is-busy" : ""}`}>
            <button
              type="button"
              className="ku-pd-back"
              onClick={() => navigate(backReturnTo)}
              aria-label="Go back"
              disabled={isLoading}
            >
              <IoArrowBack />
            </button>

            <div className="ku-pd-left">
              <div className="ku-pd-brand" aria-hidden="true">
                <div className="ku-pd-mark">
                  <img
                    src={kuptoLogo}
                    alt="Kupto"
                    className="ku-pd-logo"
                    draggable="false"
                  />
                </div>
              </div>

              <div className="ku-pd-head">
                <h1 className="ku-pd-title">
                  {googleProfile ? "Complete profile" : "Profile details"}
                </h1>

                <p className="ku-pd-sub">
                  {googleProfile ? (
                    <>
                      Finish setting up your account for <b>{maskedIdentity}</b>{" "}
                      <Link
                        className="ku-pd-miniLink"
                        to="/identification"
                        state={{ returnTo }}
                      >
                        Change
                      </Link>
                    </>
                  ) : (
                    <>
                      Creating account for <b>{maskedIdentity}</b>{" "}
                      <Link
                        className="ku-pd-miniLink"
                        to="/identification"
                        state={{ returnTo }}
                      >
                        Change
                      </Link>
                    </>
                  )}
                </p>
              </div>

              <form className="ku-pd-form" onSubmit={onSubmit} noValidate>
                <div className="ku-pd-row2">
                  <div className="ku-pd-field">
                    <div
                      className={`ku-pd-input ${
                        touched.fn && !fnOk ? "is-error" : ""
                      }`}
                    >
                      <Custominput
                        type="text"
                        name="firstname"
                        placeholder="First name"
                        value={firstname}
                        onChange={(e) => setFirstname(e.target.value)}
                        onBlur={() =>
                          setTouched((s) => ({ ...s, fn: true }))
                        }
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="ku-pd-field">
                    <div
                      className={`ku-pd-input ${
                        touched.ln && !lnOk ? "is-error" : ""
                      }`}
                    >
                      <Custominput
                        type="text"
                        name="lastname"
                        placeholder="Last name"
                        value={lastname}
                        onChange={(e) => setLastname(e.target.value)}
                        onBlur={() =>
                          setTouched((s) => ({ ...s, ln: true }))
                        }
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                </div>

                <div className="ku-pd-field ku-pd-field--full">
                  <label className="ku-pd-label">
                    Date of birth<span className="ku-pd-required">*</span>
                  </label>

                  <div
                    className={`ku-pd-bdayGroup ${
                      touched.dob && !dobOk ? "is-error" : ""
                    }`}
                  >
                    <div className="ku-pd-bdayItem">
                      <select
                        className="ku-pd-bdaySelect"
                        value={dobMonth}
                        onChange={(e) => setDobMonth(e.target.value)}
                        onBlur={() => setTouched((s) => ({ ...s, dob: true }))}
                        disabled={isLoading}
                        aria-label="Month"
                      >
                        <option value="">Month</option>
                        {MONTHS.map((m) => (
                          <option key={m.value} value={m.value}>
                            {m.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="ku-pd-bdayItem">
                      <select
                        className="ku-pd-bdaySelect"
                        value={dobDay}
                        onChange={(e) => setDobDay(e.target.value)}
                        onBlur={() => setTouched((s) => ({ ...s, dob: true }))}
                        disabled={isLoading}
                        aria-label="Day"
                      >
                        <option value="">Day</option>
                        {dayOptions.map((d) => (
                          <option key={d} value={d}>
                            {Number(d)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="ku-pd-bdayItem">
                      <select
                        className="ku-pd-bdaySelect"
                        value={dobYear}
                        onChange={(e) => setDobYear(e.target.value)}
                        onBlur={() => setTouched((s) => ({ ...s, dob: true }))}
                        disabled={isLoading}
                        aria-label="Year"
                      >
                        <option value="">Year</option>
                        {yearOptions.map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="ku-pd-bdayNote">
                    Your birthday won't be shown publicly.
                  </div>

                  {touched.dob && !dobOk ? (
                    <div className="ku-pd-error">{dobError}</div>
                  ) : null}
                </div>

                <div className="ku-pd-field ku-pd-field--full ku-pd-field--phone">
                  <label className="ku-pd-label">Mobile number</label>

                  <div
                    className={`ku-pd-phoneRow ${
                      touched.phone && !phoneOk ? "is-error" : ""
                    }`}
                  >
                    <div className="ku-pd-prefix" aria-label="Country code">
                      <div className="ku-pd-prefixBottom">{UG_PREFIX}</div>
                    </div>

                    <div className="ku-pd-phoneInput">
                      <Custominput
                        type="tel"
                        name="mobile"
                        placeholder="Phone number"
                        value={phoneLocal}
                        onChange={(e) => setPhoneLocal(e.target.value)}
                        onBlur={() => setTouched((s) => ({ ...s, phone: true }))}
                        disabled={isLoading || phoneLocked}
                      />
                    </div>
                  </div>

                  <div className="ku-pd-help">
                    Enter without +256 (example: 7XXXXXXXX). Saved as <b>{mobileE164}</b>
                    {phoneLocked ? (
                      <span className="ku-pd-verified"> • Verified</span>
                    ) : null}
                  </div>
                </div>

                {apiErr ? <div className="ku-pd-error">{apiErr}</div> : null}

                <button
                  type="submit"
                  className="ku-pd-btn"
                  disabled={!canSubmit}
                >
                  {googleProfile ? "Finish setup" : "Create account"}
                </button>

                <p className="ku-pd-legal">
                  By continuing you agree to Kupto’s{" "}
                  <Link to="/terms-and-conditions">Terms and Conditions</Link>
                </p>
              </form>
            </div>

            <div className="ku-pd-right ku-pd-right--desktopOnly">
              <h3 className="ku-pd-rightTitle">
                {googleProfile ? "Almost there" : "Tips"}
              </h3>

              <div className="ku-pd-tipList">
                <div className="ku-pd-tip">
                  <div className="ku-pd-tipIcon">🧾</div>
                  <div className="ku-pd-tipTxt">
                    <p className="ku-pd-tipH">Use real names</p>
                    <p className="ku-pd-tipP">
                      This helps with deliveries and invoices.
                    </p>
                  </div>
                </div>

                <div className="ku-pd-tip">
                  <div className="ku-pd-tipIcon">📱</div>
                  <div className="ku-pd-tipTxt">
                    <p className="ku-pd-tipH">Add a valid phone</p>
                    <p className="ku-pd-tipP">
                      We’ll contact you about delivery updates.
                    </p>
                  </div>
                </div>

                <div className="ku-pd-tip">
                  <div className="ku-pd-tipIcon">🔐</div>
                  <div className="ku-pd-tipTxt">
                    <p className="ku-pd-tipH">Keep it secure</p>
                    <p className="ku-pd-tipP">
                      {googleProfile
                        ? "Your Google account is used for sign-in."
                        : "Never share your password with anyone."}
                    </p>
                  </div>
                </div>
              </div>

              <div className="ku-pd-smallCard">
                <p className="ku-pd-smallTitle">Need help?</p>
                <p className="ku-pd-smallText">
                  Contact support if you’re stuck.
                </p>
                <Link
                  to="/contact"
                  className="ku-pd-smallBtn"
                  state={{ returnTo }}
                >
                  Contact Support
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfileDetails;